import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  styled,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add as AddIcon,
  Delete as DeleteIcon,
  Today,
  CheckCircle,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {api} from '../config/API_URL';
import { LinearProgress } from '@mui/material';
import { FileDownload } from '@mui/icons-material';
// Styled components matching your dashboard aesthetic
const StyledCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
  borderRadius: '16px',
}));

const CalendarContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  minHeight: '600px',
}));

const WeekdayHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  borderBottom: '2px solid rgba(102, 126, 234, 0.2)',
  paddingBottom: theme.spacing(1),
}));

const WeekdayCell = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1),
  textAlign: 'center',
  fontWeight: 600,
  color: '#667eea',
  fontSize: '14px',
  textTransform: 'uppercase',
}));

const WeekGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  height: '100%',
}));

const WeekRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  minHeight: '120px',
}));

const DayCell = styled(Box)(({ isToday, isSelected, isCurrentMonth, hasEvents }) => ({
  flex: 1,
  height: '100%',
  padding: '8px',
  position: 'relative',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  backgroundColor: isToday 
    ? 'rgba(102, 126, 234, 0.1)' 
    : isSelected 
    ? 'rgba(118, 75, 162, 0.05)' 
    : 'transparent',
  opacity: isCurrentMonth ? 1 : 0.4,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: isToday 
      ? 'rgba(102, 126, 234, 0.15)' 
      : 'rgba(102, 126, 234, 0.05)',
    transform: 'scale(1.01)',
  },
}));

const DayNumber = styled(Box)(({ isToday }) => ({
  padding: '4px 8px',
  fontSize: '14px',
  fontWeight: isToday ? 700 : 500,
  color: isToday ? '#fff' : '#333',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  minWidth: '32px',
  height: '32px',
  backgroundColor: isToday ? '#667eea' : 'transparent',
  marginBottom: '8px',
}));

const EventList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  overflow: 'hidden',
  maxHeight: 'calc(100% - 40px)',
}));

const EventItem = styled(Box)(({ status, eventType }) => ({
  padding: '6px 8px',
  borderRadius: '6px',
  background: 
    eventType === 'as-needed'
      ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(124, 58, 237, 0.9) 100%)' // Purple for as-needed
      : status === 'completed' 
      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)'
      : status === 'in-progress'
      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.9) 0%, rgba(217, 119, 6, 0.9) 100%)'
      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.9) 0%, rgba(220, 38, 38, 0.9) 100%)',
  color: 'white',
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(2px)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  },
}));
const parseDate = (dateString) => {
  // Parse "2025-11-14" as local date, not UTC
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const HourRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '60px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
}));

const HourLabel = styled(Box)(({ theme }) => ({
  width: '80px',
  paddingRight: theme.spacing(2),
  textAlign: 'right',
  color: '#666',
  fontSize: '13px',
  fontWeight: 600,
  borderRight: '2px solid rgba(102, 126, 234, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
}));

const HourContent = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  borderRight: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.02)',
  },
}));

const DayEvent = styled(Box)(({ isDone }) => ({
  position: 'absolute',
  left: '4px',
  right: '4px',
  background: isDone
    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.9) 0%, rgba(5, 150, 105, 0.9) 100%)'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  borderRadius: '6px',
  padding: '8px',
  fontSize: '13px',
  fontWeight: 600,
  overflow: 'hidden',
  cursor: 'pointer',
  zIndex: 2,
  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 4px 16px rgba(102, 126, 234, 0.5)',
    zIndex: 3,
  },
}));

// Task categories for color coding
const taskCategories = {
  'Personnel': '#3b82f6',
  'Access Control': '#8b5cf6',
  'Inventory': '#10b981',
  'Security': '#ef4444',
  'Operations': '#f59e0b',
  'Compliance': '#06b6d4',
  'Opening': '#667eea',
  'Mid-Day': '#764ba2',
  'Closing': '#8b5cf6',
};

// Helper functions for date handling
const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  return new Date(year, month, 1).getDay();
};

const getWeekDates = (date) => {
  const day = date.getDay();
  const diff = date.getDate() - day;
  const week = [];

  for (let i = 0; i < 7; i++) {
    const newDate = new Date(date);
    newDate.setDate(diff + i);
    week.push(newDate);
  }

  return week;
};

const isSameDay = (date1, date2) => {
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

const getHours = () => {
  const hours = [];
  for (let i = 0; i < 24; i++) {
    hours.push(i);
  }
  return hours;
};

const formatTime = (hours, minutes = 0) => {
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${suffix}`;
};

const ComplianceCalendar = () => {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [view, setView] = useState('month');
  const [selectedDate, setSelectedDate] = useState(today);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Load calendar events (completed tasks)
  useEffect(() => {
    loadCalendarData();
  }, []);

    const loadCalendarData = async () => {
    try {
        setLoading(true);
        // Use mock endpoint for now, switch to /calendar-events when ready
        const response = await api.get('/calendar-events');
        setEvents(response.data);
    } catch (err) {
        console.error('Failed to load calendar data:', err);
    } finally {
        setLoading(false);
    }
    };

  // Generate month grid with correct offset
  const generateMonthGrid = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const grid = [];

    // Previous month days
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    const daysInPrevMonth = getDaysInMonth(prevMonth);

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = new Date(prevMonth);
      day.setDate(daysInPrevMonth - i);
      grid.push({
        date: day,
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const day = new Date(currentDate);
      day.setDate(i);
      grid.push({
        date: day,
        isCurrentMonth: true
      });
    }

    // Next month days to fill the grid
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    const remainingDays = 42 - grid.length;

    for (let i = 1; i <= remainingDays; i++) {
      const day = new Date(nextMonth);
      day.setDate(i);
      grid.push({
        date: day,
        isCurrentMonth: false
      });
    }

    return grid;
  };

  const weekDates = getWeekDates(currentDate);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = parseDate(event.date);
      return isSameDay(eventDate, date);
    });
  };

  // Calculate event position for day/week view
  const calculateEventPosition = (event) => {
    const eventTime = new Date(event.completed_at || event.date);
    const startHour = eventTime.getHours() + eventTime.getMinutes() / 60;
    const duration = 1; // Default 1 hour blocks for tasks

    return {
      top: `${startHour * 60}px`,
      height: `${duration * 60}px`,
    };
  };

  // Navigation handlers
  const handlePreviousPeriod = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else if (view === 'day') {
      newDate.setDate(newDate.getDate() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNextPeriod = () => {
    const newDate = new Date(currentDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else if (view === 'day') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const handleViewChange = (event, newView) => {
    if (newView) setView(newView);
  };

  const handleOpenEventDialog = (event) => {
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedEvent(null);
  };

  // Format date for header display
  const formatHeaderDate = () => {
    const options = { year: 'numeric', month: 'long' };
    if (view === 'month') {
      return currentDate.toLocaleDateString(undefined, options);
    } else if (view === 'week') {
      const firstDay = weekDates[0];
      const lastDay = weekDates[6];
      return `${firstDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} - ${lastDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else if (view === 'day') {
      return currentDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const hours = getHours();

  return (
    <Box>
      {/* Calendar Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <StyledCard sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleToday}
                    startIcon={<Today />}
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(102, 126, 234, 0.6)',
                      },
                    }}
                  >
                    Today
                  </Button>
                  <IconButton 
                    onClick={handlePreviousPeriod}
                    sx={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.2)',
                      },
                    }}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton 
                    onClick={handleNextPeriod}
                    sx={{
                      background: 'rgba(102, 126, 234, 0.1)',
                      '&:hover': {
                        background: 'rgba(102, 126, 234, 0.2)',
                      },
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#333', ml: 1 }}>
                    {formatHeaderDate()}
                  </Typography>
                </Stack>
              </Grid>

              {/* View Tabs */}
              <Grid item xs={12}>
                <Tabs
                  value={view}
                  onChange={handleViewChange}
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: '#667eea',
                      height: 3,
                    },
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      fontSize: '15px',
                      '&.Mui-selected': {
                        color: '#667eea',
                      },
                    },
                  }}
                >
                  <Tab value="day" label="Day" />
                  <Tab value="week" label="Week" />
                  <Tab value="month" label="Month" />
                </Tabs>
              </Grid>
            </Grid>
          </CardContent>
        </StyledCard>
      </motion.div>

      {/* Calendar Component */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <StyledCard>
          <CardContent sx={{ p: 0 }}>
            <CalendarContainer>
              {/* Month View */}
              {view === 'month' && (
                <>
                  <Box sx={{ p: 2 }}>
                    <WeekdayHeader>
                      {weekdays.map(day => (
                        <WeekdayCell key={day}>{day.substring(0, 3)}</WeekdayCell>
                      ))}
                    </WeekdayHeader>
                  </Box>
                  <WeekGrid>
                    {Array.from({ length: 6 }).map((_, weekIndex) => (
                      <WeekRow key={weekIndex}>
                        {generateMonthGrid().slice(weekIndex * 7, (weekIndex + 1) * 7).map((dayData, dayIndex) => {
                          const isToday = isSameDay(dayData.date, today);
                          const isSelected = isSameDay(dayData.date, selectedDate);
                          const dayEvents = getEventsForDate(dayData.date);
                          
                          return (
                            <DayCell
                              key={dayIndex}
                              isToday={isToday}
                              isSelected={isSelected}
                              isCurrentMonth={dayData.isCurrentMonth}
                              hasEvents={dayEvents.length > 0}
                              onClick={() => {
                                setSelectedDate(dayData.date);
                                setCurrentDate(dayData.date);
                              }}
                            >
                              <DayNumber isToday={isToday}>
                                {dayData.date.getDate()}
                              </DayNumber>

                                  <EventList>
                                    {dayEvents.slice(0, 2).map((event, idx) => (
                                      <EventItem
                                        key={idx}
                                        status={event.status}
                                        eventType="daily"  // Daily tasks
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenEventDialog(event);
                                        }}
                                      >
                                        {event.status === 'completed' && <CheckCircle sx={{ fontSize: 14 }} />}
                                        {event.status === 'in-progress' && <span>⚙️</span>}
                                        {event.status === 'incomplete' && <span>✗</span>}
                                        <span>{event.title}</span>
                                      </EventItem>
                                    ))}
                                    
                                    {/* NEW: Add As-Needed Tasks */}
                                    {dayEvents[0]?.has_as_needed && (
                                      <EventItem
                                        eventType="as-needed"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenEventDialog(dayEvents[0]);
                                        }}
                                      >
                                        <span>⚡</span>
                                        <span>{dayEvents[0].as_needed_count} As-Needed Task{dayEvents[0].as_needed_count > 1 ? 's' : ''}</span>
                                      </EventItem>
                                    )}
                                    
                                    {dayEvents.length > 2 && (
                                      <Typography 
                                        variant="caption" 
                                        sx={{ 
                                          fontSize: '11px', 
                                          pl: 1, 
                                          color: '#667eea',
                                          fontWeight: 600
                                        }}
                                      >
                                        +{dayEvents.length - 2} more
                                      </Typography>
                                    )}
                                </EventList>
                            </DayCell>
                          );
                        })}
                      </WeekRow>
                    ))}
                  </WeekGrid>
                </>
              )}

              {/* Week View */}
              {view === 'week' && (
                <>
                  <Box sx={{ p: 2 }}>
                    <WeekdayHeader>
                      <Box sx={{ width: '80px' }}></Box>
                      {weekDates.map((date, index) => {
                        const isToday = isSameDay(date, today);
                        return (
                          <WeekdayCell
                            key={index}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              backgroundColor: isToday ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                              borderRadius: '8px',
                            }}
                          >
                            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '12px' }}>
                              {weekdays[index].substring(0, 3)}
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: isToday ? 700 : 500,
                                color: isToday ? '#667eea' : 'inherit',
                                fontSize: '18px',
                              }}
                            >
                              {date.getDate()}
                            </Typography>
                          </WeekdayCell>
                        );
                      })}
                    </WeekdayHeader>
                  </Box>
                  <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 400px)', minHeight: '500px' }}>
                    {hours.map(hour => (
                      <HourRow key={hour}>
                        <HourLabel>
                          {formatTime(hour)}
                        </HourLabel>
                        {weekDates.map((date, dayIndex) => {
                          const dayEvents = events.filter(event => {
                            const eventDate = new Date(event.date || event.completed_at);
                            return isSameDay(eventDate, date) &&
                              eventDate.getHours() === hour;
                          });

                          return (
                            <HourContent key={dayIndex}>
                              {dayEvents.map((event, idx) => {
                                const position = calculateEventPosition(event);
                                return (
                                  <DayEvent
                                    key={idx}
                                    isDone={event.done || event.completed}
                                    sx={{
                                      top: position.top,
                                      height: position.height
                                    }}
                                    onClick={() => handleOpenEventDialog(event)}
                                  >
                                    <Stack direction="row" alignItems="center" spacing={0.5}>
                                      {event.done && <CheckCircle sx={{ fontSize: 14 }} />}
                                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                        {formatTime(hour)}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                      {event.text || event.title}
                                    </Typography>
                                  </DayEvent>
                                );
                              })}
                            </HourContent>
                          );
                        })}
                      </HourRow>
                    ))}
                  </Box>
                </>
              )}

              {/* Day View */}
              {view === 'day' && (
                <>
                  <Box sx={{ p: 2 }}>
                    <WeekdayHeader>
                      <Box sx={{ width: '80px' }}></Box>
                      <WeekdayCell
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          backgroundColor: isSameDay(currentDate, today) ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
                          borderRadius: '8px',
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
                          {weekdays[currentDate.getDay()]}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {currentDate.toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}
                        </Typography>
                      </WeekdayCell>
                    </WeekdayHeader>
                  </Box>
                  <Box sx={{ overflowY: 'auto', height: 'calc(100vh - 400px)', minHeight: '500px' }}>
                    {hours.map(hour => {
                      const dayEvents = events.filter(event => {
                        const eventDate = new Date(event.date || event.completed_at);
                        return isSameDay(eventDate, currentDate) &&
                          eventDate.getHours() === hour;
                      });

                      return (
                        <HourRow key={hour}>
                          <HourLabel>
                            {formatTime(hour)}
                          </HourLabel>
                          <HourContent sx={{ flex: 1 }}>
                            {dayEvents.map((event, idx) => {
                              const position = calculateEventPosition(event);
                              return (
                                <DayEvent
                                  key={idx}
                                  isDone={event.done || event.completed}
                                  sx={{
                                    top: position.top,
                                    height: position.height
                                  }}
                                  onClick={() => handleOpenEventDialog(event)}
                                >
                                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                                    {event.done && <CheckCircle sx={{ fontSize: 14 }} />}
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                      {formatTime(hour)}
                                    </Typography>
                                  </Stack>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {event.text || event.title}
                                  </Typography>
                                  <Chip
                                    label={event.category || 'Task'}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      height: 20,
                                      fontSize: '10px',
                                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                      color: 'white',
                                      fontWeight: 600,
                                    }}
                                  />
                                </DayEvent>
                              );
                            })}
                          </HourContent>
                        </HourRow>
                      );
                    })}
                  </Box>
                </>
              )}
            </CalendarContainer>
          </CardContent>
        </StyledCard>
      </motion.div>

      {/* Event Detail Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          fontWeight: 700,
        }}>
          Task Details
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          {selectedEvent && (
            <Stack spacing={2}>
              {/* Status */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Status
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    icon={
                      selectedEvent.status === 'completed' ? <CheckCircle /> :
                      selectedEvent.status === 'in-progress' ? <span>⚙️</span> :
                      <span>✗</span>
                    }
                    label={
                      selectedEvent.status === 'completed' ? 'Completed' :
                      selectedEvent.status === 'in-progress' ? 'In Progress' :
                      'Incomplete'
                    }
                    sx={{
                      backgroundColor: 
                        selectedEvent.status === 'completed' ? '#10b981' :
                        selectedEvent.status === 'in-progress' ? '#f59e0b' :
                        '#ef4444',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '14px',
                    }}
                  />
                </Box>
              </Box>

              {/* Daily Tasks Progress */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Daily Tasks Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={selectedEvent.completion_percentage}
                    sx={{
                      flex: 1,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(102, 126, 234, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 
                          selectedEvent.status === 'completed' 
                            ? 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
                            : selectedEvent.status === 'in-progress'
                            ? 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
                            : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                      },
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {selectedEvent.daily_count || selectedEvent.completed_count}/{selectedEvent.total_count}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
                  {selectedEvent.daily_count || selectedEvent.completed_count} of {selectedEvent.total_count} daily tasks completed
                </Typography>
              </Box>

              {/* Daily Tasks PDF Download Button */}
              {(selectedEvent.daily_count > 0 || selectedEvent.completed_count > 0) && (
                <Button
                  variant="contained"
                  startIcon={<FileDownload />}
                  onClick={async () => {
                    try {
                      const managerName = prompt("Enter your name for the daily report:");
                      if (!managerName) return;

                      const response = await api.post('/checklist/save', {
                        completed_tasks: selectedEvent.completed_task_ids || [],
                        export_pdf: true,
                        signed_by: managerName,
                        task_type: 'daily'
                      }, {
                        responseType: 'blob' // Important: tell axios to expect a blob
                      });

                      // Create blob URL and trigger download
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `compliance-daily-${selectedEvent.date}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);

                    } catch (error) {
                      console.error('Failed to generate daily PDF:', error);
                      alert('Failed to generate daily report');
                    }
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653a8e 100%)',
                    },
                  }}
                >
                  Download Daily Report
                </Button>
              )}

              {/* As-Needed Tasks Section */}
              {selectedEvent.has_as_needed && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<FileDownload />}
                  onClick={async () => {
                    try {
                      const managerName = prompt("Enter your name for the as-needed report:");
                      if (!managerName) return;

                      const response = await api.post('/checklist/save', {
                        completed_tasks: selectedEvent.completed_task_ids || [],
                        export_pdf: true,
                        signed_by: managerName,
                        task_type: 'as-needed'
                      }, {
                        responseType: 'blob' // Important: tell axios to expect a blob
                      });

                      // Create blob URL and trigger download
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `compliance-as-needed-${selectedEvent.date}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                    } catch (error) {
                      console.error('Failed to generate as-needed PDF:', error);
                      alert('Failed to generate as-needed report');
                    }
                  }}
                  sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                    },
                  }}
                >
                  Download As-Needed Report
                </Button>
              )}

              {/* Audited Badge */}
              {selectedEvent.audited && (
                <Box sx={{ 
                  p: 2, 
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <CheckCircle sx={{ color: '#3b82f6' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                    Audited on {new Date(selectedEvent.audited_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}

              {/* Last Updated */}
              {selectedEvent.completed_at && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Last Updated
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {new Date(selectedEvent.completed_at).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComplianceCalendar;